import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import QuoteHeader from "./QuoteHeader";
import generatePDF from "../../../tools/generatePDFQuote";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { useInvoice } from "../../../InvoiceContext";
import { compressToEncodedURIComponent } from "lz-string";

function QuoteForm() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [truck, setTruck] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [distance, setDistance] = useState("");
  const [transitTime, setTransitTime] = useState("");
  const [rows, setRows] = useState([{ description: "", price: "" }]);
  const [orderNumber, setOrderNumber] = useState("");
  const [total, setTotal] = useState(0);
  const [notes, setNotes] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const { updateInvoiceData } = useInvoice();

  useEffect(() => {
    const newTotal = rows.reduce(
      (acc, row) => acc + (Number(row.price) || 0),
      0
    );
    setTotal(newTotal);
  }, [rows]);

  const handleSave = (invoice) => {
    generatePDF(invoice, orderNumber);
  };

  const handleSaveAndSend = (invoice) => {
    handleSave(invoice);

    updateInvoiceData({
      name,
      companyName,
      email,
      address,
      truck,
      origin,
      destination,
      distance,
      transitTime,
      rows,
      orderNumber,
      total,
      notes,
    });
    const compressedData = compressToEncodedURIComponent(
      JSON.stringify(invoice)
    );
    const linkToInvoiceForm = `${
      window.location.origin
    }/quote-summary?orderNumber=${encodeURIComponent(
      orderNumber
    )}&data=${compressedData}`;

    const templateParams = {
      to_email: email,
      link: linkToInvoiceForm,
    };

    console.log(linkToInvoiceForm);

    emailjs
      .send(
        "service_f9cgj1h",
        "template_rx3q2v6",
        templateParams,
        "AayvUU2KmVcoVafZF"
      )
      .then((response) => {
        console.log("Email successfully sent!", response);
        setOpenSnackbar(true);
      })
      .catch((err) => {
        console.error("There was an error sending the email:", err);
      });
  };

  const addRow = () => {
    setRows([...rows, { description: "", price: "" }]);
  };

  const updateRow = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, rowIndex) => rowIndex !== index);
    setRows(newRows);
  };

  return (
    <>
      <Button onClick={() => navigate("/")}>
        <ArrowBackIcon /> Main menu
      </Button>
      <QuoteHeader
        onOrderNumberChange={setOrderNumber}
        orderNumber={orderNumber}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave({
            name,
            companyName,
            email,
            address,
            truck,
            origin,
            destination,
            distance,
            transitTime,
            rows,
            notes,
          });
          setName("");
          setCompanyName("");
          setEmail("");
          setAddress("");
          setTruck("");
          setOrigin("");
          setDestination("");
          setDistance("");
          setTransitTime("");
          setRows([{ description: "", price: "" }]);
          setNotes("");
          setOrderNumber("");
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "16px",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <TableContainer component={Paper} style={{ width: "48%" }}>
            <Table size="small">
              <TableHead style={{ textTransform: "uppercase" }}>
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                  >
                    Customer
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Name"
                      variant="standard"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Company Name"
                      variant="standard"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Email Address"
                      variant="standard"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Address"
                      variant="standard"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      style={{ width: "100%" }}
                      multiline
                      rows={2}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper} style={{ width: "48%" }}>
            <Table size="small">
              <TableHead style={{ textTransform: "uppercase" }}>
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                  >
                    Quote description
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Truck Used"
                      variant="standard"
                      value={truck}
                      onChange={(e) => setTruck(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Origin"
                      variant="standard"
                      value={origin}
                      onChange={(e) => setOrigin(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Destination"
                      variant="standard"
                      value={destination}
                      onChange={(e) => setDestination(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Distance (miles)"
                      variant="standard"
                      value={distance}
                      onChange={(e) => setDistance(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      label="Transit time"
                      variant="standard"
                      value={transitTime}
                      onChange={(e) => setTransitTime(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
            flexDirection: "column",
          }}
        >
          <TableContainer component={Paper} style={{ width: "100%" }}>
            <Table size="small">
              <TableHead style={{ textTransform: "uppercase" }}>
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                  >
                    Price
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <TextField
                        required
                        label=""
                        variant="standard"
                        value={row.description}
                        onChange={(e) =>
                          updateRow(index, "description", e.target.value)
                        }
                        style={{ width: "100%" }}
                        InputProps={{
                          disableUnderline: true,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        required
                        label=""
                        variant="standard"
                        type="number"
                        value={row.price}
                        onChange={(e) =>
                          updateRow(index, "price", e.target.value)
                        }
                        style={{ width: "100%" }}
                        InputProps={{
                          disableUnderline: true,
                          startAdornment: (
                            <AttachMoneyIcon
                              style={{ fontSize: 16 }}
                            ></AttachMoneyIcon>
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => deleteRow(index)}
                      >
                        <DeleteIcon></DeleteIcon>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div style={{ marginTop: "10px" }}>
            <Button onClick={addRow} variant="contained">
              Add
            </Button>
          </div>
        </div>
        <TableContainer
          component={Paper}
          style={{
            maxWidth: "150px",
            marginBottom: "10px",
            justifyContent: "flex-end",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    fontWeight: "bold",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell style={{ textAlign: "center" }}>
                  ${total.toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <TableContainer component={Paper} style={{ width: "100%" }}>
            <Table size="small">
              <TableHead style={{ textTransform: "uppercase" }}>
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                  >
                    Notes
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <TextField
                      required
                      multiline
                      rows={2}
                      label=""
                      variant="standard"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      style={{ width: "100%" }}
                      InputProps={{
                        disableUnderline: true,
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <TableContainer component={Paper} style={{ width: "100%" }}>
            <Table size="small">
              <TableHead style={{ textTransform: "uppercase" }}>
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                  >
                    Special Notes and instructions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    Quote includes: Business to Business transportation, fuel,
                    permits, escorts. <br></br>Insurance for units being
                    transported. Open communication and updates 24/7. <br></br>
                    Coordination and logistics prior to pickup, while transport
                    and after delivery as well. Premium service during entire
                    transaction. <br></br>Above information is an estimate of
                    services/goods described above. Payment will be collected in
                    prior to provision of services/goods described in this
                    quote.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <TableContainer component={Paper} style={{ width: "100%" }}>
            <Table size="small">
              <TableHead style={{ textTransform: "uppercase" }}>
                <TableRow>
                  <TableCell
                    style={{ textAlign: "center", fontWeight: "bold" }}
                    colSpan={2}
                  >
                    Payment Methods
                  </TableCell>
                </TableRow>

                <TableCell
                  style={{
                    textAlign: "center",
                    marginBottom: "auto",
                  }}
                  colSpan={2}
                >
                  <p>
                    IF YOUR PAYMENT IS VIA WIRE/ACH TRANSFER OR ZELLE PLEASE
                    MAKE SURE TO ADD THIS INVOICE # TO YOUR PAYMENT REFERENCE
                    NOTES
                  </p>
                </TableCell>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell
                    style={{
                      width: "50%",
                      flexDirection: "column",
                    }}
                  >
                    <h4
                      style={{
                        textTransform: "uppercase",
                        textAlign: "center",
                        marginBottom: "auto",
                      }}
                    >
                      Direct deposit and ACH
                    </h4>
                    <p>
                      Global Transit Solutions LLC <br></br> <b>Bank:</b> Chase
                      Bank <br></br> <b>Account Number:</b> 530532616 <br></br>{" "}
                      <b>Routing:</b> 322271627 <br></br> 5600 Wilshire blvd 01
                      Los Angeles, CA 90036
                    </p>
                  </TableCell>
                  <TableCell
                    style={{
                      width: "50%",
                    }}
                  >
                    <h4
                      style={{
                        textTransform: "uppercase",
                        textAlign: "center",
                        marginBottom: "auto",
                      }}
                    >
                      Wire transfers / International transfers
                    </h4>
                    <p>
                      Global Transit Solutions LLC <br></br> <b>Bank:</b> Chase
                      Bank <br></br> <b>Account Number:</b> 530532616 <br></br>{" "}
                      <b>Routing:</b> 021000021 <br></br> <b>Swift Code:</b>{" "}
                      CHASUS33
                      <br></br> 5600 Wilshire blvd 01 Los Angeles, CA 90036
                    </p>
                  </TableCell>
                </TableRow>
              </TableBody>
              <TableRow>
                <TableCell
                  style={{
                    textAlign: "left",
                    marginBottom: "auto",
                  }}
                >
                  <p>
                    <b> Zelle: </b> admin@globaltransitsol.com <br></br>{" "}
                    <b>Credit / Debit Cards:</b> We take all cards at a 4%
                    processing fee
                  </p>
                </TableCell>
              </TableRow>
            </Table>
          </TableContainer>
        </div>

        <div
          style={{
            textAlign: "center",
            margin: "20px 0",
            fontFamily: "Lato, sans-serif",
            fontWeight: "bold",
            fontSize: "15 px",
          }}
        >
          Thank you for your business!
        </div>

        <div
          style={{
            background: "black",
            color: "white",
            padding: "10px",
            width: "100%",
            textAlign: "center",
          }}
        ></div>

        {/* Button Save */}
        <Button type="submit" variant="outlined">
          Save
        </Button>

        {/* Button Save and Send */}
        <Button
          onClick={(e) => {
            e.preventDefault();
            handleSaveAndSend({
              name,
              companyName,
              email,
              address,
              truck,
              origin,
              destination,
              distance,
              transitTime,
              rows,
              notes,
            });
            setName("");
            setCompanyName("");
            setEmail("");
            setAddress("");
            setTruck("");
            setOrigin("");
            setDestination("");
            setDistance("");
            setTransitTime("");
            setRows([{ description: "", price: "" }]);
            setNotes("");
            setOrderNumber("");
          }}
          variant="outlined"
        >
          Save and Send
        </Button>
      </form>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={1500}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success">
          Invoice form sent correctly.
        </Alert>
      </Snackbar>
    </>
  );
}

export default QuoteForm;
